import {
  BillingViewModel,
  BuildFeeViewModel,
  FeatureRequestViewModel,
  LeadViewModel,
  PaymentViewModel,
  SiteExportViewModel,
  SubscribeViewModel,
  TicketListViewModel,
  UsageViewModel,
} from "@lumora/core";
import type { BillingStatus, SiteExportDownload } from "@lumora/bridge";
import { GetStartedForm, navRoutes, useViewModel } from "@lumora/ui";
import { useEffect, useMemo } from "react";
import { HashRouter, Link, Navigate, Route, Routes, useLocation, useSearchParams } from "react-router-dom";
import { SITE, STRIPE_PUBLISHABLE_KEY, client, signOutOwner } from "./client.js";
import { downloadJsonFile } from "./downloadJsonFile.js";
import { SiteLogo } from "./SiteLogo.js";
import { SITE_ROUTES } from "./routes.js";
import { LumoraAboutPage } from "./views/AboutPage.js";
import { LandingPage } from "./views/LandingPage.js";
import { PortalPage } from "./views/PortalPage.js";
import { AppsPage } from "./views/AppsPage.js";
import { PricingPage } from "./views/PricingPage.js";
import { PrivacyPage } from "./views/PrivacyPage.js";
import { TermsPage } from "./views/TermsPage.js";
import { DevelopersPage } from "./views/DevelopersPage.js";
import { PlatformGuard } from "./views/platform/PlatformGuard.js";
import { PlatformPaymentsPage } from "./views/platform/PlatformPaymentsPage.js";
import { PlatformActivityPage } from "./views/platform/PlatformActivityPage.js";
import { PlatformBillingPage } from "./views/platform/PlatformBillingPage.js";
import { PlatformOpsPage } from "./views/platform/PlatformOpsPage.js";
import { PlatformSignInPage } from "./views/platform/PlatformSignInPage.js";
import { PlatformSitesPage } from "./views/platform/PlatformSitesPage.js";
import { PlatformSettingsPage } from "./views/platform/PlatformSettingsPage.js";
import { SiteBackendPage } from "./views/platform/SiteBackendPage.js";
import { PlatformShell } from "./views/platform/PlatformShell.js";
import { ADMIN_SITE, isPlatformAdmin } from "./platformClient.js";
import { marketingSurfaceForPath } from "./marketingSurface.js";
import { OwnerAcceptPage } from "./views/OwnerAcceptPage.js";
import { LAST_SITE_KEY, SignInPage } from "./views/SignInPage.js";
import { OwnerFeatureRequestPage } from "./views/OwnerFeatureRequestPage.js";

function saveSiteExportFile(download: SiteExportDownload): void {
  const blob = new Blob([JSON.stringify(download.document)], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = download.filename;
  link.click();
  URL.revokeObjectURL(url);
}

/** Quoted one-off build amount from owner billing status. Zero hides pay. */
export function quotedBuildFeeCents(
  status: Pick<BillingStatus, "buildQuoteCents"> | null | undefined,
): number {
  const cents = status?.buildQuoteCents ?? 0;
  return cents > 0 ? cents : 0;
}

/** Public marketing redirects — exported for route tests (#625). */
export function MarketingRedirectRoutes() {
  return (
    <>
      <Route path="/signup" element={<Navigate to="/get-started" replace />} />
      <Route path="/packages" element={<Navigate to="/pricing" replace />} />
      <Route path="/services" element={<Navigate to="/pricing" replace />} />
      <Route path="/contact" element={<Navigate to="/about" replace />} />
    </>
  );
}

export function MarketingNav({
  isAuth,
  isAdmin,
  quietChrome,
}: {
  isAuth: boolean;
  isAdmin: boolean;
  quietChrome: boolean;
}) {
  const ctaClass = quietChrome ? "site-nav-link" : "site-nav-cta";

  return (
    <nav className="site-nav" aria-label="Site">
      <div className="site-nav-primary">
        {navRoutes(SITE_ROUTES).map((r) => (
          <Link key={r.path} to={r.path}>
            {r.label}
          </Link>
        ))}
      </div>
      <div className="site-nav-actions">
        {isAdmin ? (
          <Link to="/platform/ops" className="site-nav-admin">
            Platform
          </Link>
        ) : null}
        {!isAuth && !isAdmin ? (
          <>
            <Link to="/get-started" className="site-nav-cta">
              Get started
            </Link>
            <Link to="/signin" className="site-nav-link site-nav-link--secondary">
              Sign in
            </Link>
          </>
        ) : null}
        {isAuth && !isAdmin ? (
          <>
            <Link to="/dashboard" className={ctaClass}>
              Dashboard
            </Link>
            <button
              type="button"
              className="site-nav-signout"
              onClick={() => {
                signOutOwner();
                window.location.hash = "#/signin";
                window.location.reload();
              }}
            >
              Sign out
            </button>
          </>
        ) : null}
      </div>
    </nav>
  );
}

export function App() {
  return (
    <HashRouter>
      <AppRoutes />
    </HashRouter>
  );
}

/** Platform console routes. Call as `{PlatformRoutes()}` — `<Routes>` only sees `<Route>` children. */
export function PlatformRoutes() {
  return (
    <Route
      path="/platform"
      element={
        <PlatformGuard>
          <PlatformShell />
        </PlatformGuard>
      }
    >
      <Route index element={<Navigate to="ops" replace />} />
      <Route path="ops" element={<PlatformOpsPage />} />
      <Route path="activity" element={<PlatformActivityPage />} />
      <Route path="payments" element={<PlatformPaymentsPage />} />
      <Route path="billing" element={<PlatformBillingPage />} />
      <Route path="settings" element={<PlatformSettingsPage />} />
      <Route path="sites" element={<PlatformSitesPage />} />
      <Route path={`sites/${ADMIN_SITE}`} element={<Navigate to="/platform/sites" replace />} />
      <Route path="sites/:slug" element={<SiteBackendPage />} />
      <Route path="*" element={<Navigate to="ops" replace />} />
    </Route>
  );
}

function leadPrefillFromSearch(searchParams: URLSearchParams) {
  const prefill: {
    serviceTier?: string;
    packageTier?: string;
    photographyAddon?: boolean;
    addonKeys?: string[];
  } = {};
  const service = searchParams.get("service");
  const pkg = searchParams.get("package");
  if (service) prefill.serviceTier = service;
  if (pkg) prefill.packageTier = pkg;
  if (searchParams.get("photography") === "1") prefill.photographyAddon = true;
  const addons = searchParams.get("addons");
  if (addons) {
    prefill.addonKeys = addons
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return prefill;
}

function createLeadViewModel(
  prefill: ReturnType<typeof leadPrefillFromSearch>,
  options?: { intakeForm?: boolean },
) {
  return new LeadViewModel(
    async (lead, tierPrefill) => {
      await client.submitLead({
        site: SITE,
        businessName: lead.businessName,
        email: lead.email,
        phone: lead.phone,
        projectDescription: lead.projectDescription,
        imageKeys: [...lead.imageKeys],
        ...(lead.domainStatus ? { domainStatus: lead.domainStatus } : {}),
        ...(tierPrefill.serviceTier ? { serviceTier: tierPrefill.serviceTier } : {}),
        ...(tierPrefill.packageTier ? { packageTier: tierPrefill.packageTier } : {}),
        ...(tierPrefill.photographyAddon ? { photographyAddon: true } : {}),
      });
    },
    async (file) =>
      client.presignLeadUpload(SITE, file.name, file.type || "application/octet-stream", file.size),
    prefill,
    options,
  );
}

function AppRoutes() {
  const [searchParams] = useSearchParams();
  const leadVM = useMemo(
    () => createLeadViewModel(leadPrefillFromSearch(searchParams)),
    [searchParams],
  );
  const getStartedVM = useMemo(
    () => createLeadViewModel(leadPrefillFromSearch(searchParams), { intakeForm: true }),
    [searchParams],
  );
  const featureVM = useMemo(() => new FeatureRequestViewModel(client), []);

  const ticketsVM = useMemo(() => new TicketListViewModel(client), []);
  const usageVM = useMemo(() => new UsageViewModel(client), []);
  const billingVM = useMemo(
    () => new BillingViewModel(client, (url) => { window.location.href = url; }),
    [],
  );
  const siteExportVM = useMemo(
    () => new SiteExportViewModel(client, saveSiteExportFile),
    [],
  );
  const subscribeVM = useMemo(() => new SubscribeViewModel(client), []);
  const paymentVM = useMemo(() => new PaymentViewModel(), []);
  const billing = useViewModel(billingVM);
  const buildFeeVM = useMemo(
    () => new BuildFeeViewModel(client, quotedBuildFeeCents(billing.status)),
    [billing.status],
  );
  const isAuth = Boolean(client.claims());
  const ownerSiteSlug =
    (typeof localStorage !== "undefined" && localStorage.getItem(LAST_SITE_KEY)) || undefined;

  useEffect(() => {
    if (isAuth) {
      void ticketsVM.load();
      void usageVM.load();
    }
  }, [ticketsVM, usageVM, isAuth]);
  const isAdmin = isPlatformAdmin();
  const location = useLocation();
  const platformConsole =
    location.pathname.startsWith("/platform") && location.pathname !== "/platform/signin";
  const surface = marketingSurfaceForPath(location.pathname);
  const quietChrome = surface === "utility";

  useEffect(() => {
    document.documentElement.dataset.surface = surface;
    return () => {
      delete document.documentElement.dataset.surface;
    };
  }, [surface]);

  return (
    <>
      {!platformConsole ? (
        <header className={`site-header site-header--${surface}`}>
          <SiteLogo />
          <MarketingNav isAuth={isAuth} isAdmin={isAdmin} quietChrome={quietChrome} />
        </header>
      ) : null}
      <main
        className={
          platformConsole
            ? "site-main site-main--platform"
            : `site-main site-main--${surface}`
        }
      >
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route
            path="/dashboard"
            element={
              isAuth ? (
                <PortalPage
                  className="site-dashboard"
                  subscribeVM={subscribeVM}
                  paymentVM={paymentVM}
                  buildFeeVM={buildFeeVM}
                  ticketsVM={ticketsVM}
                  usageVM={usageVM}
                  billingVM={billingVM}
                  siteExportVM={siteExportVM}
                  publishableKey={STRIPE_PUBLISHABLE_KEY}
                  returnUrl={window.location.href}
                  siteSlug={ownerSiteSlug}
                />
              ) : (
                <Navigate to="/signin" replace />
              )
            }
          />
          <Route
            path="/pricing"
            element={<PricingPage className="site-packages site-packages--wide" />}
          />
          <Route path="/apps" element={<AppsPage />} />
          <Route path="/terms" element={<TermsPage className="site-pricing" />} />
          <Route path="/privacy" element={<PrivacyPage className="site-pricing" />} />
          <Route path="/developers" element={<DevelopersPage className="site-pricing" />} />
          <Route path="/about" element={<LumoraAboutPage viewModel={leadVM} />} />
          <Route
            path="/get-started"
            element={<GetStartedForm className="site-get-started" viewModel={getStartedVM} />}
          />
          {MarketingRedirectRoutes()}
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/owner/accept" element={<OwnerAcceptPage />} />
          <Route
            path="/__/request"
            element={
              isAuth ? (
                <OwnerFeatureRequestPage viewModel={featureVM} />
              ) : (
                <Navigate to="/signin" replace />
              )
            }
          />
          <Route path="/platform/signin" element={<PlatformSignInPage />} />
          {PlatformRoutes()}
        </Routes>
      </main>
    </>
  );
}
